import { AxiosInstance } from 'axios';
import {
  BulkApproveRequest,
  BulkApproveResultItem,
  BulkEditItemSpecsRequest,
  BulkEditResult,
  BulkMergeRequest,
  EditItemSpecsRequest,
  ExtractRequest,
  ExtractResponse,
  ExtractionJobsResponse,
  ImportCyberportRequest,
  ImportCyberportResult,
  MergeRequest,
  MergeResult,
  PickerProductLine,
  PickerProductModel,
  PickerVocabularyEntry,
  ResourceType,
  ReviewQueueFilters,
  ReviewQueueResponse,
  SingleEditResult,
  SpecState,
} from './specifications.types';

const BASE = '/v1/admin';

const RESOURCE_PATH: Record<ResourceType, string> = {
  productLine: 'product-lines',
  model: 'product-models',
  vocabulary: 'spec-vocabulary',
};

export const createSpecificationsApi = (api: AxiosInstance) => ({
  reviewQueue: (filters?: ReviewQueueFilters) =>
    api
      .get<ReviewQueueResponse>(`${BASE}/review/queue`, { params: filters })
      .then((r) => r.data),

  extract: (body: ExtractRequest) =>
    api
      .post<ExtractResponse>(`${BASE}/items/specifications/extract`, body)
      .then((r) => r.data),

  extractionJobs: (status?: string) =>
    api
      .get<ExtractionJobsResponse>(`${BASE}/items/specifications/extract/jobs`, {
        params: { status },
      })
      .then((r) => r.data),

  approve: (resource: ResourceType, id: number) =>
    api
      .post(`${BASE}/${RESOURCE_PATH[resource]}/${id}/approve`)
      .then((r) => r.data),

  merge: (resource: ResourceType, id: number, body: MergeRequest) =>
    api
      .post<MergeResult>(`${BASE}/${RESOURCE_PATH[resource]}/${id}/merge`, body)
      .then((r) => r.data),

  reject: (resource: ResourceType, id: number) =>
    api.delete(`${BASE}/${RESOURCE_PATH[resource]}/${id}`).then((r) => r.data),

  approveBranch: (productLineId: number) =>
    api
      .post(`${BASE}/review/approve-branch`, { productLineId })
      .then((r) => r.data),

  bulkApprove: (body: BulkApproveRequest) =>
    api
      .post<BulkApproveResultItem[]>(`${BASE}/review/bulk-approve`, body)
      .then((r) => r.data),

  bulkMerge: (body: BulkMergeRequest) =>
    api.post(`${BASE}/review/bulk-merge`, body).then((r) => r.data),

  pickerProductLines: (params: {
    manufacturerId?: number;
    category?: string;
    state?: SpecState;
    search?: string;
  }) =>
    api
      .get<PickerProductLine[]>(`${BASE}/product-lines`, { params })
      .then((r) => r.data),

  pickerProductModels: (params: {
    productLineId?: number;
    state?: SpecState;
    search?: string;
  }) =>
    api
      .get<PickerProductModel[]>(`${BASE}/product-models`, { params })
      .then((r) => r.data),

  pickerVocabulary: (params: {
    manufacturerId?: number;
    attribute?: string;
    state?: SpecState;
    search?: string;
  }) =>
    api
      .get<PickerVocabularyEntry[]>(`${BASE}/spec-vocabulary`, { params })
      .then((r) => r.data),

  editItem: (itemId: string, body: EditItemSpecsRequest) =>
    api
      .patch<SingleEditResult>(
        `${BASE}/items/${encodeURIComponent(itemId)}/specifications`,
        body,
      )
      .then((r) => r.data),

  bulkEditItems: (body: BulkEditItemSpecsRequest) =>
    api
      .post<BulkEditResult>(`${BASE}/items/specifications/bulk-edit`, body)
      .then((r) => r.data),

  importCyberport: (body: ImportCyberportRequest) =>
    api
      .post<ImportCyberportResult>(
        `${BASE}/items/specifications/imports/cyberport`,
        body,
        // The Cyberport dump is ~36 MB; allow generous transfer + parse time.
        { timeout: 5 * 60_000, maxContentLength: Infinity, maxBodyLength: Infinity },
      )
      .then((r) => r.data),
});
